import * as z from 'zod';
import { create } from 'zustand';
import type { I18n } from './i18n';
import { persist } from 'zustand/middleware';
import { simpleHash } from './simple-hash';
import { isServer } from '@tanstack/react-query';

export const detailsSchema = (i18n: I18n, portalId: string) =>
	z
		.object({
			firstname: z.string().min(1, i18n.errors.required),
			lastname: z.string().min(1, i18n.errors.required),
			email: z.email(i18n.errors.email),
			repeatEmail: z.email(i18n.errors.email),
			certificate: z.boolean(),
			documentType: z.enum(['id', 'passport', 'taxId', 'nie', 'others'], i18n.errors.documentType),
			documentNumber: z.string(),
			streetName: z.string(),
			streetNumber: z.string(),
			additionalInfo: z.string().or(z.undefined()),
			city: z.string(),
			cp: z.string(),
			country: z.string(),
			region: z.string(),
			province: z.string(),
			termsAndConditions: z.boolean().refine((val) => val, i18n.errors.termsAndConditions),
			ngosTermsAndConditions: z.boolean(),
			telefonicaTermsAndConditions: z.boolean(),
		})
		.check((ctx) => {
			if (ctx.value.repeatEmail !== ctx.value.email) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.emailsDoNotMatch,
					path: ['repeatEmail'],
					input: ctx.value,
				});
			}

			if (!ctx.value.documentNumber && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['documentNumber'],
					input: ctx.value,
				});
			}

			if (!ctx.value.streetName && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['streetName'],
					input: ctx.value,
				});
			}

			if (!ctx.value.streetNumber && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['streetNumber'],
					input: ctx.value,
				});
			}

			if (!ctx.value.city && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['city'],
					input: ctx.value,
				});
			}

			if (!ctx.value.cp && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['cp'],
					input: ctx.value,
				});
			}

			if (!ctx.value.country && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['country'],
					input: ctx.value,
				});
			}

			if (!ctx.value.region && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['region'],
					input: ctx.value,
				});
			}

			if (!ctx.value.province && (ctx.value.certificate || portalId === 'telefonica')) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['province'],
					input: ctx.value,
				});
			}

			if (
				ctx.value.country !== 'AD' &&
				ctx.value.region === '' &&
				(ctx.value.certificate || portalId === 'telefonica')
			) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['region'],
					input: ctx.value,
				});
			}

			if (
				ctx.value.country !== 'AD' &&
				ctx.value.province === '' &&
				(ctx.value.certificate || portalId === 'telefonica')
			) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.required,
					path: ['province'],
					input: ctx.value,
				});
			}

			if (!ctx.value.telefonicaTermsAndConditions && portalId === 'telefonica') {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.termsAndConditions,
					path: ['telefonicaTermsAndConditions'],
					input: ctx.value,
				});
			}

			if (!ctx.value.ngosTermsAndConditions && ctx.value.certificate) {
				ctx.issues.push({
					code: 'custom',
					message: i18n.errors.termsAndConditions,
					path: ['ngosTermsAndConditions'],
					input: ctx.value,
				});
			}
		});

const termsContentSchema = z
	.object({
		checked: z.boolean(),
		text: z.string(),
		label: z.string(),
	})
	.optional();

export const usePersistedStore = create<{
	_hasHydrated: boolean;
	setHasHydrated: (state: boolean) => void;
	amount: number | undefined;
	setAmount: (amount: number | undefined) => void;
	details: z.infer<ReturnType<typeof detailsSchema>>;
	setDetails: (
		data:
			| z.infer<ReturnType<typeof detailsSchema>>
			| ((prev: z.infer<ReturnType<typeof detailsSchema>>) => z.infer<ReturnType<typeof detailsSchema>>),
	) => void;
	termsContent: z.infer<typeof termsContentSchema>;
	setTermsContent: (data: z.infer<typeof termsContentSchema>) => void;
	ngosTermsContent: z.infer<typeof termsContentSchema>;
	setNgosTermsContent: (data: z.infer<typeof termsContentSchema>) => void;
	telefonicaTermsContent: z.infer<typeof termsContentSchema>;
	setTelefonicaTermsContent: (data: z.infer<typeof termsContentSchema>) => void;
}>()(
	persist(
		(set) => ({
			_hasHydrated: false,
			setHasHydrated: (state) => {
				set({
					_hasHydrated: state,
				});
			},
			amount: undefined,
			setAmount: (amount) => set(() => ({ amount })),
			details: {
				firstname: '',
				lastname: '',
				email: '',
				repeatEmail: '',
				certificate: false,
				documentType: 'id',
				documentNumber: '',
				streetName: '',
				streetNumber: '',
				additionalInfo: '',
				city: '',
				cp: '',
				country: 'ES',
				province: '',
				region: '',
				termsAndConditions: false,
				ngosTermsAndConditions: false,
				telefonicaTermsAndConditions: false,
			},
			setDetails: (details) =>
				set((state) => ({ details: typeof details === 'function' ? details(state.details) : details })),
			termsContent: undefined,
			setTermsContent: (termsContent) => set(() => ({ termsContent })),
			ngosTermsContent: undefined,
			setNgosTermsContent: (ngosTermsContent) => set(() => ({ ngosTermsContent })),
			telefonicaTermsContent: undefined,
			setTelefonicaTermsContent: (telefonicaTermsContent) => set(() => ({ telefonicaTermsContent })),
		}),
		{
			name: `app-storage-${hashVersion(detailsSchema.toString())}`,
			onRehydrateStorage: (state) => {
				return () => state.setHasHydrated(true);
			},
		},
	),
);

function hashVersion(str: string) {
	if (isServer) return 'version';
	return simpleHash(str);
}

export const useEphemeralStore = create<{
	pagesLoaded: string[];
	setPagesLoaded: (pagesLoaded: string[]) => void;
	donationToConfirm: { id: string; campaignId: string } | undefined;
	setDonationToConfirm: (donationToConfirm: { id: string; campaignId: string } | undefined) => void;
	donationConfirmed: boolean;
	setDonationConfirmed: (donationConfirmed: boolean) => void;
	cachedPreregistration: { donationId: string; paymentToken: string; captchaPassed: boolean } | undefined;
	setCachedPreregistration: (
		data: { donationId: string; paymentToken: string; captchaPassed: boolean } | undefined,
	) => void;
	cachedPaymentMethods: unknown;
	setCachedPaymentMethods: (data: unknown) => void;
	clearCheckoutCache: () => void;
}>((set) => ({
	pagesLoaded: [],
	setPagesLoaded: (pagesLoaded: string[]) => set({ pagesLoaded }),
	donationToConfirm: undefined,
	setDonationToConfirm: (donationToConfirm) => set(() => ({ donationToConfirm })),
	donationConfirmed: false,
	setDonationConfirmed: (donationConfirmed) => set(() => ({ donationConfirmed })),
	cachedPreregistration: undefined,
	setCachedPreregistration: (cachedPreregistration) => set(() => ({ cachedPreregistration })),
	cachedPaymentMethods: undefined,
	setCachedPaymentMethods: (cachedPaymentMethods) => set(() => ({ cachedPaymentMethods })),
	clearCheckoutCache: () =>
		set(() => ({
			cachedPreregistration: undefined,
			cachedPaymentMethods: undefined,
		})),
}));
